import React, { Fragment, useEffect, useRef, useState } from "react";
import Helmet from "react-helmet";
import { get } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { Form } from "@unform/web";
import { useLocation } from "react-router-dom";
import tvgConf from "@tvg/conf";
import RegularPageRegistration from "@tvg/atomic-ui/_templates/RegularPageRegistration";
import Button from "@tvg/atomic-ui/_atom/Buttons/button";
import { fontMedium } from "@tvg/atomic-ui/_static/Typography";
import { REGISTRATION_STEPS } from "@tvg/atomic-ui/_static/Constants/progressBar";
import InputV2 from "@tvg/atomic-ui/_molecule/InputV2";
import parseCAPIMessage from "@tvg/utils/capiUtils";
import buildColor from "@tvg/atomic-ui/_static/ColorPalette";
import { handleBackButtonHeader } from "../../utils/handlers";
import { setCurrentStepRegistrationAction } from "../../actions";
import { onVirtualPageViewGtmEvent } from "../../utils/gtm/pageViews";

import { onClickContinueBtnGtmEvent } from "../../utils/gtm/formCompletion";
import useAutoAddressSetup from "../../hooks/useAutoAddressSetup";
import { onBackButtonClickGtmEvent } from "../../utils/gtm/otherInteractions";

export const AutoAddressComponent = ({ device }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const enableRegAutoAddress = useSelector((store) =>
    get(store, "capi.featureToggles.enableRegAutoAddress", false)
  );
  const currentStep = useSelector((store) =>
    get(store, "registration.currentStep", "WELCOME")
  );
  const isEditing = useSelector((store) =>
    get(store, "registration.isEditing")
  );
  const labelAddressError = useSelector((store) =>
    get(
      parseCAPIMessage(store, "capi.messages.registrationTexts"),
      "address.home.errors.streetEmpty",
      ""
    )
  );
  const formRef = useRef(null);
  const [hasDefinedAutoComplete, setHasDefinedAutoComplete] = useState(false);

  useEffect(() => {
    const view = isEditing
      ? "edit-auto-address-details"
      : "auto-address-details";
    onVirtualPageViewGtmEvent(location, view);
  }, []);

  useEffect(() => {
    if (!enableRegAutoAddress) {
      dispatch(setCurrentStepRegistrationAction(REGISTRATION_STEPS.ADDRESS));
    }
  }, [enableRegAutoAddress, isEditing]);

  useAutoAddressSetup({
    dispatch,
    hasDefinedAutoComplete,
    setHasDefinedAutoComplete,
    isEditing,
    formRef
  });

  const handleSubmit =
    () =>
    ({ address }) => {
      onClickContinueBtnGtmEvent("Auto-Address");

      if (!address) {
        formRef.current.setFieldError("address", labelAddressError);
      } else {
        formRef.current.setFieldError("address", "");
        dispatch(setCurrentStepRegistrationAction(REGISTRATION_STEPS.ADDRESS));
      }
    };

  return (
    <Fragment>
      <Helmet>
        <style>{`
          .pac-container {
            border: 1px solid ${buildColor("blue", "100")};
            padding-bottom: 8px;
          }
          
          .pac-container::after {
            margin-right: 8px;
          }
        
          .pac-item {
            font-family: 'Roboto-Regular', Arial, sans-serif;
            height: 42px;
            display: flex;
            align-items: center;
            padding: 0 8px;
          }
          
          .pac-item:hover {
            background-color: ${buildColor("blue_accent", "100")}
          }
          
          .pac-icon {
            display: none;
          }
          
          .pac-item-query {
            font-family: 'Roboto-Regular', Arial, sans-serif;
            font-weight: 400;
            color: ${buildColor("grey", "900")};
          }
          
          .pac-matched {
            padding-bottom: 2px;
            font-family: 'Roboto-Medium', Arial, sans-serif;
            font-weight: 500;
          }
        `}</style>
      </Helmet>

      <RegularPageRegistration
        device={device}
        brand={tvgConf().brand}
        handleBackButton={() => {
          onBackButtonClickGtmEvent(
            isEditing ? "Edit auto address" : "Auto address"
          );
          if (isEditing) {
            dispatch(
              setCurrentStepRegistrationAction(REGISTRATION_STEPS.REVIEW)
            );
          } else {
            handleBackButtonHeader({ dispatch, currentStep });
          }
        }}
        currentStep={isEditing ? REGISTRATION_STEPS.REVIEW : currentStep}
        innerTitle="Address"
        infoText="Enter the address listed on your bank statements or utility bills."
      >
        <Form ref={formRef} onSubmit={handleSubmit()}>
          <InputV2
            name="address"
            label="Search Address"
            qaLabel="auto-address-text"
            placeholder="Start typing your address..."
            marginBottom={12}
            autoFocus
          />
          <Button
            size="bigger"
            type="tertiary"
            isStretched
            isUppercase={false}
            fontFamily={fontMedium}
            isBold={false}
            customMarginBottom={20}
            onClick={() => {
              dispatch(
                setCurrentStepRegistrationAction(REGISTRATION_STEPS.ADDRESS)
              );
            }}
            qaLabel="registration-continue-address-btn"
          >
            Enter address manually
          </Button>
          <Button
            size="bigger"
            hasRoundedCorners
            isStretched
            isUppercase={false}
            hasShadow
            fontFamily={fontMedium}
            isBold={false}
            qaLabel="registration-submit-btn"
          >
            Continue
          </Button>
        </Form>
      </RegularPageRegistration>
    </Fragment>
  );
};

export default AutoAddressComponent;
